import { type Employee, EmployeeListInput, type EmployeeListQuery } from "@cleanbudget/contract/employee";
import { IconPlus } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { ButtonLink } from "@/components/button-link";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DataTableEmpty } from "@/components/data-table/empty";
import { DataTablePagination } from "@/components/data-table/pagination";
import { DataTableToolbar, FilterSelect, SearchInput } from "@/components/data-table/toolbar";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { formatDate, formatDateTime } from "@/lib/format";
import { orpc } from "@/lib/orpc";
import { statusTone } from "./-components/status";

const employeesQuery = (query: EmployeeListQuery) => orpc.employee.list.queryOptions({ input: query });

/** The list's defaults, left out of the URL so a plain `/settings/employee` is the default view. */
const { sort, order, page, pageSize } = EmployeeListInput.parse({});

/**
 * Medewerkers: the first screen on the data table. Search, status filter, sort and page are the
 * URL's search params, validated by the contract's own list input, so they survive a refresh and
 * travel in a link.
 */
export const Route = createFileRoute("/_admin/settings/employee/")({
	validateSearch: EmployeeListInput,
	search: { middlewares: [stripSearchParams({ sort, order, page, pageSize })] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) => context.queryClient.ensureQueryData(employeesQuery(deps)),
	component: EmployeesPage,
});

function EmployeesPage() {
	const { t } = useTranslation("employee");
	const { t: tCommon } = useTranslation();
	const { t: tSettings } = useTranslation("settings");
	const search = Route.useSearch();
	const { data } = useSuspenseQuery(employeesQuery(search));

	const columns: readonly Column<Employee>[] = [
		{
			id: "name",
			header: t("column.name"),
			sortable: true,
			cell: (employee) => (
				<span className="flex flex-col">
					<Link
						to="/settings/employee/$id"
						params={{ id: employee.id }}
						className="font-medium underline-offset-4 hover:underline"
					>
						{employee.name}
					</Link>
					{/* On a phone the email column is hidden; show the address under the name. */}
					<span className="text-xs text-muted-foreground md:hidden">{employee.email}</span>
				</span>
			),
		},
		{
			id: "email",
			header: t("column.email"),
			sortable: true,
			secondary: true,
			cell: (employee) => employee.email,
		},
		{
			id: "status",
			header: t("column.status"),
			cell: (employee) => (
				<StatusBadge tone={statusTone(employee.status)}>{t(`status.${employee.status}`)}</StatusBadge>
			),
		},
		{
			id: "passkeys",
			header: t("column.passkeys"),
			secondary: true,
			align: "right",
			cell: (employee) => employee.passkeyCount,
		},
		{
			id: "lastLoginAt",
			header: t("column.lastLogin"),
			sortable: true,
			secondary: true,
			cell: (employee) =>
				employee.lastLoginAt ? (
					<time dateTime={employee.lastLoginAt}>{formatDateTime(employee.lastLoginAt)}</time>
				) : (
					<span className="text-muted-foreground">{tCommon("never")}</span>
				),
		},
		{
			id: "createdAt",
			header: t("column.created"),
			sortable: true,
			secondary: true,
			cell: (employee) => <time dateTime={employee.createdAt}>{formatDate(employee.createdAt)}</time>,
		},
	];

	const filtered = Boolean(search.search) || search.status !== undefined;
	const pastEnd = data.items.length === 0 && data.total > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				back={<BackLink to="/settings">{tSettings("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
				actions={
					<ButtonLink to="/settings/employee/new">
						<IconPlus aria-hidden />
						{t("new.title")}
					</ButtonLink>
				}
			/>
			<div className="flex flex-col gap-3">
				<DataTableToolbar>
					<SearchInput value={search.search} label={t("searchLabel")} />
					<FilterSelect
						param="status"
						label={t("status.label")}
						value={search.status}
						options={[
							{ value: "active", label: t("status.active") },
							{ value: "invited", label: t("status.invited") },
							{ value: "deactivated", label: t("status.deactivated") },
						]}
					/>
				</DataTableToolbar>
				<DataTable
					label={t("title")}
					columns={columns}
					rows={data.items}
					rowKey={(employee) => employee.id}
					sort={search.sort}
					order={search.order}
					empty={<DataTableEmpty filtered={filtered} pastEnd={pastEnd} nothingYet={t("empty")} />}
				/>
				<DataTablePagination page={data.page} pageSize={data.pageSize} total={data.total} />
			</div>
		</div>
	);
}
